import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/auth'
import {router} from '@/router'

export interface ApiResponse<T = any> {
  success: boolean
  data: T
  message: string
  errorCode?: string
  timestamp: string
}

class ApiService {
  private axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: this.getApiBaseUrl(),
      timeout: 30000, // 30초로 증가
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      withCredentials: false // CORS 설정에 맞춤
    })

    this.setupInterceptors()
  }

  private getApiBaseUrl(): string {
    // 프로덕션 환경에서는 환경변수 사용
    if (import.meta.env.PROD) {
      return import.meta.env.VITE_API_BASE_URL || 'https://api.marevobeachhotel.com'
    }
    
    // 개발환경: 브라우저의 현재 도메인/IP 기반으로 API URL 생성
    const protocol = window.location.protocol // http: 또는 https:
    const hostname = window.location.hostname // IP 또는 도메인명
    const backendPort = '8080' // 백엔드 서버 포트
    
    const dynamicUrl = `${protocol}//${hostname}:${backendPort}`
      
    // 개발환경에서 디버깅을 위한 로깅
    if (import.meta.env.DEV) {
      console.log(`[API Service] Dynamic API Base URL: ${dynamicUrl}`)
      console.log(`[API Service] Browser Location: ${window.location.protocol}//${window.location.host}`)
    }
    
    return dynamicUrl
  }

  private setupInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // JWT 토큰 추가
        const token = localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        
        // CSRF 토큰 추가 (필요시)
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        if (csrfToken) {
          config.headers['X-CSRF-TOKEN'] = csrfToken
        }
        
        // 요청 로깅 (개발환경에서만)
        if (import.meta.env.DEV) {
          console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data)
        }
        
        return config
      },
      (error) => {
        console.error('[API Request Error]', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 응답 로깅 (개발환경에서만)
        if (import.meta.env.DEV) {
          console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data)
        }
        
        // 표준 응답 형태로 변환
        if (response.data && typeof response.data === 'object' && 'success' in response.data) {
          return response.data
        }
        
        // 레거시 응답 형태 처리
        return {
          success: true,
          data: response.data,
          message: 'Success',
          timestamp: new Date().toISOString()
        }
      },
      async (error) => {
        // 에러 로깅
        console.error('[API Response Error]', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data
        })
        
        // 401 Unauthorized 처리
        if (error.response?.status === 401) {
          const refreshToken = localStorage.getItem('refreshToken')
          if (refreshToken && !error.config._retry) {
            error.config._retry = true
            
            try {
              const response = await this.axiosInstance.post('/api/v1/auth/refresh', {
                refreshToken
              })
              
              if (response.success) {
                localStorage.setItem('token', response.data.token)
                localStorage.setItem('refreshToken', response.data.refreshToken)
                
                // 원래 요청 재시도
                error.config.headers.Authorization = `Bearer ${response.data.token}`
                return this.axiosInstance.request(error.config)
              }
            } catch (refreshError) {
              console.error('[Token Refresh Error]', refreshError)
              this.logout()
            }
          } else {
            this.logout()
          }
        }
        
        // 에러 응답 표준화
        const errorResponse = {
          success: false,
          data: null,
          message: error.response?.data?.message || error.message || '서버 오류가 발생했습니다.',
          errorCode: error.response?.data?.errorCode || `HTTP_${error.response?.status}`,
          timestamp: new Date().toISOString()
        }
        
        return Promise.reject(errorResponse)
      }
    )
  }

  private logout() {
    // 로컬 스토리지 정리
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.removeItem('theme')
    localStorage.removeItem('miniSidebar')

    // Pinia 스토어 정리
    try {
      const authStore = useAuthStore()
      authStore.logout()
    } catch (e) {
      console.warn('Auth store not available during logout')
    }

    // 현재 경로에 따라 적절한 로그인 페이지로 리다이렉트
    const currentPath = window.location.pathname
    if (currentPath.startsWith('/pre-check-in')) {
      // 사전 체크인 흐름
      router.push('/pre-check-in')
    } else if (currentPath.startsWith('/room/') || currentPath.startsWith('/check-in')) {
      // 객실 서비스 또는 일반 체크인 흐름
      router.push('/check-in')
    } else {
      // 관리자 페이지
      router.push('/manage/login')
    }
  }

  // HTTP 메소드들
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.get(url, config)
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.post(url, data, config)
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.put(url, data, config)
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.patch(url, data, config)
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.axiosInstance.delete(url, config)
  }
  
  // 파일 업로드 전용 메소드
  async uploadFile<T>(url: string, file: File, additionalData?: any): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key])
      })
    }
    
    return this.axiosInstance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
  
  // 다중 파일 업로드 메소드
  async uploadFiles<T>(url: string, files: File[], additionalData?: any): Promise<ApiResponse<T>> {
    const formData = new FormData()
    
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file)
    })
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key])
      })
    }
    
    return this.axiosInstance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
  
  // 파일 다운로드 메소드
  async downloadFile(url: string, filename?: string): Promise<void> {
    const response = await this.axiosInstance.get(url, {
      responseType: 'blob'
    })
    
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }
  
  // 요청 취소를 위한 AbortController 생성
  createCancelToken(): AbortController {
    return new AbortController()
  }
}

// API 서비스 인스턴스 생성 및 내보내기
export const apiService = new ApiService()

// 기본 내보내기
export default apiService

// 타입 재내보내기
export type { ApiResponse }